import {
    AfterContentInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
    inject,
} from '@angular/core';
import { FormArray, UntypedFormGroup } from '@angular/forms';
import { NgbAccordionDirective } from '@ng-bootstrap/ng-bootstrap';
import { WdxButtonStyle, WdxButtonSize } from '@wdx/shared/components/wdx-button';
import { WdxSplitButtonMenuItem } from '@wdx/shared/components/wdx-split-button';
import {
    AuditMode,
    ExternalTaskStatusType,
    FeatureFlag,
    FeaturesService,
    FormActionType,
    FormCompletionSummary,
    FormDataResult,
    FormDataStatus,
    FormDefinition,
    FormFieldType,
    FormFunctionResult,
    FormInitialisationMode,
    FormLayout,
    FormLock,
    FormSaveMode,
    FormStateData,
    MenuItem,
    POP_UP_TRIGGER,
    Privilege,
    SummaryLevel,
    TRANSLATION_BY_LABEL,
    TRANSLATION_CANCEL_BTN,
    TRANSLATION_ENUM_FORMDATASTATUS_DRAFT,
    TRANSLATION_ENUM_FORMDATASTATUS_HISTORICDRAFT,
    TRANSLATION_ENUM_FORMDATASTATUS_HISTORICPUBLISHED,
    TRANSLATION_ENUM_FORMDATASTATUS_PUBLISHED,
    TRANSLATION_FORM_CLICK_HERE_BTN,
    TRANSLATION_FORM_CLICK_TO_VIEW_VERSION_BTN,
    TRANSLATION_FORM_ERROR_COMPLETE_MISSING_FIELDS,
    TRANSLATION_FORM_EXTERNAL_USER_FORM_DATA_CHANGES,
    TRANSLATION_FORM_FIELDS_COMPLETED,
    TRANSLATION_FORM_FRAMEWORK_LAYOUT_ERROR,
    TRANSLATION_FORM_INFO_VERSION_OF_THIS_FORM,
    TRANSLATION_FORM_INFO_YOU_ARE_VIEWING,
    TRANSLATION_FORM_NO_REQUIRED_FIELDS,
    TRANSLATION_OF_LABEL,
    TRANSLATION_POPOVER_BACK_TO_FORM,
    TRANSLATION_POPOVER_FORM_HISTORY,
    TRANSLATION_SAVE_AND_COMPLETE_BTN,
    TRANSLATION_SAVE_AS_DRAFT_BTN,
    TRANSLATION_SAVE_BTN,
    TRANSLATION_SOMETHING_WENT_WRONG,
    TRANSLATION_SVG_TEXT_NO_RECORD_FOUND,
    TranslationsService,
    UTILS_ICON_AUDIT,
    UTILS_ICON_CHEVRON_LEFT,
    WdxDestroyClass,
} from '@wdx/shared/utils';
import {
    debounceTime,
    distinctUntilChanged,
    takeUntil,
    tap,
    Observable,
    take,
    delay,
    filter,
} from 'rxjs';

import { DEFAULT_FORM_STYLING } from '../../../constants';
import { ARRAY_CONTROL_TYPES } from '../../../constants/control-types';
import {
    FormConfig,
    IFormExternalActions,
    ITriggerExternalFormSave,
    ITriggerFunction,
} from '../../../interfaces';
import {
    FormFrameworkDirtyEvent,
    FormFrameworkEvent,
    FormFrameworkFormTouchedEvent,
    FormFrameworkFormValueChangedEvent,
    FormFrameworkInitialisedEvent,
    FormFrameworkSaveClickedEvent,
    FormFrameworkSaveDraftClickedEvent,
    FormStylingOptions,
    LayoutAndDefinitionEntry,
    ValidationSummaryError,
    ValidationSummarySection,
} from '../../../models';
import { FormActionButton } from '../../../models/form-action-button.model';
import {
    FormBuilderService,
    FormConditionsService,
    FormControlService,
    FormLockService,
    FormProcessConditionDataService,
    FormStaticService,
    FormSummaryLayoutService,
    FormValidationService,
    SummaryInfoCard,
} from '../../../services';
import { FormFieldsDataService } from '../../../services/form-fields-data';
import { FormPendingChangesService } from '../../../services/form-pending-changes/form-pending-changes.service';
import * as R from 'ramda';
import { FormFirstLevelService } from '../../../services/form-builder/form-first-level.service';
import { FormArrayService } from '../../../services/form-array/form-array.service';
import { FormApiFunctionService } from '../../../services/form-api-function/form-api-function.service';

@Component({
    selector: 'wdx-ff-form',
    templateUrl: './form-framework-form.component.html',
    styleUrls: ['./form-framework-form.component.scss'],
    providers: [
        FormBuilderService,
        FormConditionsService,
        FormValidationService,
        FormStaticService,
        FormProcessConditionDataService,
        FormControlService,
        FormPendingChangesService,
        FormFieldsDataService,
        FormFirstLevelService,
        FormArrayService,
        FormApiFunctionService,
    ],
    standalone: false,
    host: { class: 'd-flex flex-column h-100 overflow-hidden' },
})
export class FormFrameworkFormComponent
    extends WdxDestroyClass
    implements OnInit, AfterContentInit
{
    public fbService = inject(FormBuilderService);
    public condService = inject(FormConditionsService);
    private formArrayService = inject(FormArrayService);
    public formPendingChangesService = inject(FormPendingChangesService);
    private formStaticService = inject(FormStaticService);
    private formSummaryLayoutService = inject(FormSummaryLayoutService);
    private formValidationService = inject(FormValidationService);
    private elementRef = inject(ElementRef);
    private formLockService = inject(FormLockService);
    private externalActionsHandler = inject(IFormExternalActions);
    private formFieldsDataService = inject(FormFieldsDataService);
    private formControlService = inject(FormControlService);
    protected translationsService = inject(TranslationsService);
    private formProcessConditionDataService = inject(
        FormProcessConditionDataService,
    );
    private firstLevel = inject(FormFirstLevelService).firstLevel;

    @Input() formId!: string;
    @Input() entityId!: string;
    @Input() appId!: string;
    @Input() formDefinition!: FormDefinition;
    @Input() formLayout!: FormLayout;
    @Input() formData!: FormDataResult;
    @Input() formConfig?: FormConfig;
    @Input() isQuickCreate = false;
    @Input() isLoading!: boolean;
    @Input() hasError!: boolean;
    @Input() isSaving!: boolean;
    @Input() modalInstanceId?: string;
    @Input() set stylingOptions(options: FormStylingOptions) {
        this.formStylingOptions = {
            ...DEFAULT_FORM_STYLING,
            ...options,
        };
    }
    @Input() auditMode: AuditMode = AuditMode.Off;

    private _readonlyMode = false;
    @Input() set readonlyMode(value: boolean) {
        this.formStaticService.isReadonlyMode = value;
        this._readonlyMode = value;
    }
    get readonlyMode() {
        return this._readonlyMode;
    }

    @Input() set triggerFunction(value: ITriggerFunction | undefined) {
        if (value?.functionNames?.length) {
            this.condService.externallyTriggerFunctions(
                value.functionNames,
                value.subFormName,
                value.subFormIndex,
            );
        }
    }

    @Input() set triggerSave(value: ITriggerExternalFormSave | undefined) {
        if (value?.id) {
            this.onSaveClick(undefined, value);
        }
    }
    @Input() set triggerSaveDraft(value: ITriggerExternalFormSave | undefined) {
        if (value?.id) {
            this.onDraftSaveClick(value);
        }
    }
    @Input() set refreshFormData(formData: FormDataResult | undefined) {
        if (formData) {
            this.formData = formData;
            this.condService.reset();
            this.setupForm(this.isExternalTaskSubmitted);
        }
    }
    @Input() set formFunctionResult(
        value:
            | {
                  result: FormFunctionResult;
                  functionName: string;
              }
            | undefined,
    ) {
        if (value?.result) {
            this.condService.updateFunctionChanges(
                value.result,
                value.functionName,
            );
        }
    }

    @Input() formTouchedByUser = false;
    @Input() extraContext?: Record<string, any>;
    @Input() hiddenSections?: number[];
    @Input() hideVersionSwitcher = false;
    @Input() set patchFormValue(data: any) {
        if (data) {
            Object.entries(data).forEach(([name, value]) => {
                if (Array.isArray(value)) {
                    this.formArrayService.updateArrayToLength(
                        name,
                        value.length,
                    );
                }
                const control = this.formStaticService.form.controls[name];
                control.markAsDirty();
                this.formControlService.applyFuncToAllFormControlDescendants(
                    control,
                    (formControl) => {
                        formControl?.markAsDirty();
                    },
                );
            });
            this.formStaticService.form.patchValue(data);
        }
    }
    @Input() extraConfig?: { subformValueChangeAlwaysEmit?: boolean };
    @Output() formTitleReceived = new EventEmitter<string | null>();
    @Output() cancelClicked = new EventEmitter();
    @Output() saveClicked = new EventEmitter<FormStateData>();
    @Output() saveDraftClicked = new EventEmitter<FormStateData>();
    @Output() completionSummaryChanged =
        new EventEmitter<FormCompletionSummary>();
    @Output() formEvent = new EventEmitter<FormFrameworkEvent>();
    @Output() formFunction = new EventEmitter<{
        formFunction: string;
        formData: Record<string, any>;
    }>();

    @ViewChild('accordion', { read: NgbAccordionDirective })
    accordion!: NgbAccordionDirective;

    validationSummary!: ValidationSummarySection[];
    completionSummary!: FormCompletionSummary;
    formStylingOptions!: FormStylingOptions;
    layoutError = false;

    form!: UntypedFormGroup;
    sectionsInError!: string[];

    fullFormLock!: boolean;
    saveMode!: boolean;
    saveCompleteMode!: boolean;
    saveDraftMode!: boolean;
    isDraftForm!: boolean;
    isPublishedForm!: boolean;
    showFormSwitcherPanel!: boolean;
    showFormHistory = false;

    draftForm!: FormDataResult;
    publishedForm!: FormDataResult;
    validationMessage!: string;
    activePanelIds!: string[];
    submitAttempted = false;
    WdxButtonStyle = WdxButtonStyle;
    WdxButtonSize = WdxButtonSize;
    AuditMode = AuditMode;
    public layoutAndDefinition$ = this.condService.layoutAndDefinition$.pipe(
        tap((layoutAndDefinition) => {
            this.layoutAndDefinition = layoutAndDefinition;
        }),
    );
    public layoutAndDefinition?: readonly LayoutAndDefinitionEntry[];

    get infoCards$(): Observable<SummaryInfoCard[]> {
        return this.formSummaryLayoutService.summaryInfoCards$;
    }

    readonly ExternalTaskStatusType = ExternalTaskStatusType;
    readonly FormFieldType = FormFieldType;
    readonly ARRAY_CONTROL_TYPES = ARRAY_CONTROL_TYPES;
    readonly PRIVILEGE = Privilege;
    readonly FORM_DATA_STATUS = FormDataStatus;
    readonly POP_UP_TRIGGER = POP_UP_TRIGGER;
    readonly ICON_FORM_HISTORY = UTILS_ICON_AUDIT;
    readonly ICON_BACK = UTILS_ICON_CHEVRON_LEFT;
    readonly POPOVER_FORM_HISTORY = TRANSLATION_POPOVER_FORM_HISTORY;
    readonly POPOVER_BACK_TO_FORM = TRANSLATION_POPOVER_BACK_TO_FORM;
    readonly SOMETHING_WENT_WRONG = TRANSLATION_SOMETHING_WENT_WRONG;
    readonly SAVE_AS_DRAFT_BTN = TRANSLATION_SAVE_AS_DRAFT_BTN;
    readonly CANCEL_BTN = TRANSLATION_CANCEL_BTN;
    readonly SAVE_AND_COMPLETE_BTN = TRANSLATION_SAVE_AND_COMPLETE_BTN;
    readonly SAVE_BTN = TRANSLATION_SAVE_BTN;
    readonly NO_REQUIRED_FIELDS = TRANSLATION_FORM_NO_REQUIRED_FIELDS;
    readonly OF_LABEL = TRANSLATION_OF_LABEL;
    readonly FIELDS_COMPLETED = TRANSLATION_FORM_FIELDS_COMPLETED;
    readonly CLICK_HERE_BTN = TRANSLATION_FORM_CLICK_HERE_BTN;
    readonly CLICK_TO_VIEW_VERSION_BTN =
        TRANSLATION_FORM_CLICK_TO_VIEW_VERSION_BTN;
    readonly FORM_INFO_YOU_ARE_VIEWING = TRANSLATION_FORM_INFO_YOU_ARE_VIEWING;
    readonly BY = TRANSLATION_BY_LABEL;
    readonly VERSION_OF_THIS_FORM = TRANSLATION_FORM_INFO_VERSION_OF_THIS_FORM;
    readonly FORM_EXTERNAL_USER_FORM_DATA_CHANGES =
        TRANSLATION_FORM_EXTERNAL_USER_FORM_DATA_CHANGES;
    readonly FORM_FRAMEWORK_LAYOUT_ERROR =
        TRANSLATION_FORM_FRAMEWORK_LAYOUT_ERROR;
    readonly SVG_TEXT_NO_RECORD_FOUND = TRANSLATION_SVG_TEXT_NO_RECORD_FOUND;

    splitButtonMenuItems: MenuItem[] = [
        {
            label: this.translationsService.getTranslationByKey(
                this.SAVE_AS_DRAFT_BTN,
            ) as any,
        },
    ];

    get messageDefinition() {
        return this.formFieldsDataService?.messageDefinitionToShow;
    }

    get messageDefinitionLength() {
        return Object.keys(this.formFieldsDataService?.messageDefinitionToShow)
            ?.length;
    }

    constructor() {
        super();
        this.formArrayService.setFormConditionsService(this.condService);
        this.formProcessConditionDataService.setFormArrayService(
            this.formArrayService,
        );
    }

    private featuresService = inject(FeaturesService);

    get isExternalTaskSubmitted(): boolean {
        return (
            this.formData?.context?.activityExternalTask?.externalStatus ===
                ExternalTaskStatusType.Submitted &&
            this.featuresService.hasFeature(FeatureFlag.PortalLockKycFormOnSubmission)
        );
    }

    ngOnInit() {
        this.setupForm(this.isExternalTaskSubmitted);
        this.setModes();
        this.setUpSummary();
        this.setDraftModeProps();

        this.condService.layoutAndDefinition$
            .pipe(
                filter((layoutAndDef) => Boolean(layoutAndDef.length)),
                take(1),
                tap((layoutAndDef) => {
                    if (
                        !this.readonlyMode &&
                        this.formConfig?.createSubformsToMeetMin
                    ) {
                        this.createSubformsToMeetMin(layoutAndDef);
                    }
                }),
                delay(0),
            )
            .subscribe(() => {
                this.condService.setupPendingChanges(
                    this.firstLevel,
                    this.formStaticService?.formData?.pendingChanges,
                );
            });

        this.validationMessage = this.saveDraftMode
            ? this.translationsService.getTranslationByKey(
                  TRANSLATION_FORM_ERROR_COMPLETE_MISSING_FIELDS,
              )
            : this.translationsService.getTranslationByKey(
                  TRANSLATION_FORM_ERROR_COMPLETE_MISSING_FIELDS,
              );

        this.formStaticService.form.valueChanges
            .pipe(
                distinctUntilChanged((x, y) => R.equals(x, y)),
                takeUntil(this.formStaticService.destroyed$),
                debounceTime(300),
            )
            .subscribe((value) => {
                if (!this.readonlyMode) {
                    this.layoutError = false;
                    this.formEvent.emit(
                        new FormFrameworkFormValueChangedEvent({
                            dirty: this.form.dirty,
                            pristine: this.form.pristine,
                            touched: this.form.touched,
                            valid:
                                !this.form.invalid &&
                                !this.validationSummary?.length,
                            formValue: value,
                            validationSummary: this.validationSummary,
                        }),
                    );
                    if (this.form.dirty) {
                        this.formEvent.emit(new FormFrameworkDirtyEvent({}));
                    }
                }
            });

        if (this.isQuickCreate) {
            this.externalActionsHandler.handleQuickCreate();
        }
    }

    ngAfterContentInit() {
        this.formEvent.emit(
            new FormFrameworkInitialisedEvent({
                dirty: this.form.dirty,
                pristine: this.form.pristine,
                touched: this.form.touched,
                valid: !this.form.invalid && !this.validationSummary?.length,
                formValue: this.form.value,
                formValueChanges$: this.form.valueChanges,
                actions: this.formData.actions,
                formDefinition: this.formDefinition,
                layoutAndDefinition$: this.condService.layoutAndDefinition$,
                initialFormData: structuredClone(this.formData.data),
                context: this.formData.context,
                infoCards$: this.infoCards$,
            }),
        );
        this.showFormVersionSwitcher();
        this.storeFormSwitchData();
    }

    setupForm(forceFormLock = false): void {
        this.formTitleReceived.emit(this?.formLayout?.label);
        this.formStaticService.formDefinition = this.formDefinition;
        this.formStaticService.formLayout = this.formLayout;
        this.formStaticService.formData = this.formData;
        this.formStaticService.formId = this.formId;

        if (forceFormLock) {
            this.formStaticService.isReadonlyMode = true;
        }

        this.formStaticService.initialisationMode = this.formData?.entityId
            ? FormInitialisationMode.Edit
            : FormInitialisationMode.Add;
        this.fbService.buildForm(this.extraContext);
        this.form = this.formStaticService.form;

        this.condService.configService(
            this.formStaticService.form,
            this.formStaticService.formDefinition,
            this.formStaticService.formData,
            this.formStaticService.formId,
            this.formStaticService.fieldsToNullCheck,
            this.formStaticService.initialisationMode,
            {
                subformValueChangeAlwaysEmit:
                    this.extraConfig?.subformValueChangeAlwaysEmit,
            },
        );

        this.activePanelIds = Array.from(
            {
                length: (
                    this.layoutAndDefinition ??
                    this.condService.layoutAndDefinition
                )?.length,
            },
            (_, i) => `reactive-form-${i}`,
        );

        this.fullFormLock = this.getFullFormLock(
            this.formData?.lock as FormLock,
            forceFormLock,
        );

        if (this.formData.lock || forceFormLock) {
            this.applyFormLock(this.formData.lock as FormLock, forceFormLock);
        }

        this.calculateValidationSummary();

        this.formStaticService.form.statusChanges
            .pipe(takeUntil(this.destroyed$), debounceTime(200))
            .subscribe(() => this.calculateValidationSummary());
    }

    getFullFormLock(lock: FormLock, forceFormLock = false) {
        return forceFormLock || this.formLockService.hasFullFormLock(lock);
    }

    /**
     * Apply form lock by disabling the entire or individual form fields
     */
    applyFormLock(lock: FormLock, forceFormLock = false): void {
        // Update the models in condition service (so lock status can be referenced from definition)
        this.condService.updateLockStatuses(forceFormLock);

        // Check for form lock status and disable entire form if true
        if (this.formLockService.hasFullFormLock(lock) || forceFormLock) {
            this.formStaticService.form.disable();
            return;
        }

        // For partial locks, disable the associated form controls
        if (this.formLockService.hasPartialFormLock(lock)) {
            lock.fields?.forEach((field) => {
                const controlToLock = this.formStaticService?.form?.get(field);
                if (controlToLock) {
                    controlToLock.disable();
                }
            });
        }
    }

    setUpSummary() {
        this.formSummaryLayoutService.getSummaryInfoCards(
            this.formDefinition,
            this.formStaticService.form.getRawValue(),
        );

        const summaryFieldNames = (
            this.formStaticService.formDefinition.schema?.filter(
                (item) => item.summaryLevel === SummaryLevel.InfoCard,
            ) || []
        ).map((summaryField) => summaryField.name as string);

        summaryFieldNames.forEach((summaryFieldName) => {
            this.formStaticService.form?.controls[
                summaryFieldName
            ]?.valueChanges.subscribe(() => {
                this.formSummaryLayoutService.getSummaryInfoCards(
                    this.formDefinition,
                    this.formStaticService.form.getRawValue(),
                );
            });
        });

        this.condService.fieldValueUpdated$
            .pipe(
                filter((fieldName) => summaryFieldNames.includes(fieldName)),
                takeUntil(this.destroyed$),
            )
            .subscribe(() => {
                this.formSummaryLayoutService.getSummaryInfoCards(
                    this.formDefinition,
                    this.formStaticService.form.getRawValue(),
                );
            });
    }

    setModes(): void {
        this.saveMode = !!this.formDefinition?.saveModes?.includes(
            FormSaveMode.Save,
        );

        this.saveCompleteMode = !!this.formDefinition?.saveModes?.includes(
            FormSaveMode.Complete,
        );
    }

    setDraftModeProps(): void {
        this.saveDraftMode = !!this.formDefinition?.saveModes?.includes(
            FormSaveMode.Draft,
        );
        this.isDraftForm =
            this.formData?.status === this.FORM_DATA_STATUS.Draft;
        this.isPublishedForm =
            this.formData?.status === this.FORM_DATA_STATUS.Published;
    }

    showFormVersionSwitcher(): void {
        this.showFormSwitcherPanel =
            !this.hideVersionSwitcher &&
            this.saveDraftMode &&
            this.isDraftForm &&
            this.formData?.lastPublishedData;
    }

    storeFormSwitchData(): void {
        if (this.formData?.status === FormDataStatus.Draft) {
            this.draftForm = this.formData;
        }

        if (this.formData?.lastPublishedData) {
            this.publishedForm = this.formData.lastPublishedData;
        }
    }

    onFormAction(
        formActionButton: FormActionButton | WdxSplitButtonMenuItem,
    ): void {
        switch (formActionButton.type) {
            case 'Cancel':
                this.onCancelClick();
                break;
            case FormActionType.Complete:
                this.onSaveClick('complete=true');
                break;
            case FormActionType.Function:
                this.onFormFunctionClick(formActionButton.action?.function);
                break;
            case FormActionType.Publish:
                this.onSaveClick();
                break;
            case FormActionType.SaveDraft:
                this.onDraftSaveClick();
                break;
        }
    }

    /**
     * When form is cancelled, reset form to initial state
     */
    onCancelClick(): void {
        if (this.isQuickCreate) {
            this.formStaticService.form.reset();
        }
        this.cancelClicked.emit();
    }

    switchFormVersion() {
        const formStatus: FormDataStatus = this.formData
            ?.status as FormDataStatus;

        if (formStatus === FormDataStatus.Draft) {
            this.formData = this.publishedForm;
            this.condService.reset();
            this.setupForm(true);
            this.setDraftModeProps();
        } else if (formStatus === FormDataStatus.Published) {
            this.formData = this.draftForm;
            this.condService.reset();
            this.setupForm(false);
            this.setDraftModeProps();
        }
    }

    onSaveClick(
        queryParams?: string,
        externalConfig?: ITriggerExternalFormSave,
    ): void {
        this.layoutError = false;
        if (this.formStaticService.form.invalid) {
            if (!this.formStaticService.submitAttempted$.isStopped) {
                this.formStaticService.form.markAllAsTouched();
                this.formStaticService.form.updateValueAndValidity();
                this.formStaticService.submitAttempted$.next(true);
                this.formStaticService.submitAttempted$.complete();
                this.submitAttempted = true;
            }

            if (!this.validationSummary?.length) {
                this.layoutError = true;
            }

            this.formEvent.emit(
                new FormFrameworkSaveClickedEvent({
                    valid: false,
                    externallyTriggered: externalConfig?.externallyTriggered,
                    saveId: externalConfig?.id,
                }),
            );
            return;
        }

        this.formEvent.emit(
            new FormFrameworkSaveClickedEvent({
                valid: true,
                externallyTriggered: externalConfig?.externallyTriggered,
                saveId: externalConfig?.id,
            }),
        );

        if (this.formStaticService.form.valid) {
            this.saveClickEmit(queryParams as string);
        }
    }

    saveClickEmit(queryParams: string): void {
        this.completionSummaryChanged.emit(this.completionSummary);
        this.saveClicked.emit({
            value: this.formFieldsDataService.parseFormData(
                this.formStaticService.fieldsToNullCheck,
                this.formStaticService.form,
            ),
            queryParams: queryParams,
        });
    }

    onDraftSaveClick(externalConfig?: ITriggerExternalFormSave): void {
        this.formEvent.emit(
            new FormFrameworkSaveDraftClickedEvent({
                externallyTriggered: externalConfig?.externallyTriggered,
                saveId: externalConfig?.id,
            }),
        );
        this.completionSummaryChanged.emit(this.completionSummary);
        this.saveDraftClicked.emit({
            value: this.formFieldsDataService.parseFormData(
                this.formStaticService.fieldsToNullCheck,
                this.formStaticService.form,
            ),
            saveId: externalConfig?.id,
            silent: externalConfig?.silent,
        });
    }

    onFormFunctionClick(formFunction?: string): void {
        if (!formFunction) {
            return;
        }
        this.formFunction.emit({
            formFunction,
            formData: this.formFieldsDataService.parseFormData(
                this.formStaticService.fieldsToNullCheck,
                this.formStaticService.form,
            ),
        });
    }

    calculateValidationSummary() {
        const validationSummary =
            this.formValidationService.getValidationSummary(
                this.layoutAndDefinition ??
                    this.condService.layoutAndDefinition,
                this.formStaticService.form.controls,
            );

        this.validationSummary = validationSummary.sections.filter(
            (section) => section?.errors?.length > 0,
        );

        this.completionSummary =
            this.formValidationService.getCompletionSummary(
                this.formStaticService.form,
            );

        this.sectionsInError = this.validationSummary.map(
            (section) => section.name,
        ) as string[];
    }

    onErrorClicked(error: ValidationSummaryError): void {
        this.accordion?.expand(`reactive-form-${error.sectionIndex}`);

        // delay scroll to allow accordion to expand first
        setTimeout(() => {
            const fieldDefinitionElementRef =
                this.elementRef.nativeElement.querySelector(
                    `[data-form-control='${error.name}']`,
                );
            this.formValidationService.scrollToError(fieldDefinitionElementRef);
        }, 100);
    }

    openLinkInNewTab(entity: string, id?: string): void {
        this.externalActionsHandler.handleOpenInNewTab(entity, id);
    }

    toggleFormHistory(): void {
        this.showFormHistory = !this.showFormHistory;
    }

    get formDataStatus(): string {
        const formDataStatus = this.formData?.status;

        switch (formDataStatus) {
            case FormDataStatus.Draft:
                return TRANSLATION_ENUM_FORMDATASTATUS_DRAFT;
            case FormDataStatus.Published:
                return TRANSLATION_ENUM_FORMDATASTATUS_PUBLISHED;
            case FormDataStatus.HistoricDraft:
                return TRANSLATION_ENUM_FORMDATASTATUS_HISTORICDRAFT;
            case FormDataStatus.HistoricPublished:
                return TRANSLATION_ENUM_FORMDATASTATUS_HISTORICPUBLISHED;

            default:
                return formDataStatus ?? '';
        }
    }

    formElementClicked(event: Event) {
        if (this.formTouchedByUser) {
            return;
        }
        this.formTouchedByUser = true;
        this.formEvent.emit(
            new FormFrameworkFormTouchedEvent({
                dirty: this.form.dirty,
                pristine: this.form.pristine,
                touched: this.form.touched,
                valid: !this.form.invalid && !this.validationSummary?.length,
                formValue: this.form.value,
                event,
            }),
        );
    }

    private createSubformsToMeetMin(layoutAndDef: LayoutAndDefinitionEntry[]) {
        const subFormsNotMeetingMin = layoutAndDef.flatMap(
            ({ layoutAndDefinition }) =>
                layoutAndDefinition?.filter(
                    ({ fieldType, min, name }) =>
                        fieldType === FormFieldType.Array &&
                        Boolean(min) &&
                        (this.form.controls[name as string] as FormArray)
                            .controls.length < (min as number),
                ),
        );
        subFormsNotMeetingMin.forEach((subform) =>
            this.formArrayService.updateArrayToLength(
                subform!.name as string,
                subform?.min as number,
            ),
        );
    }
}
